import { LanguageProvider } from '@/contexts/LanguageContext';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import CustomCursor from '@/components/CustomCursor';
import AlexChat from '@/components/AlexChat';
import HeroSection from '@/components/sections/HeroSection';
import TickerSection from '@/components/sections/TickerSection';
import ServicesSection from '@/components/sections/ServicesSection';
import CxTeaserSection from '@/components/sections/CxTeaserSection';
import NumbersSection from '@/components/sections/NumbersSection';
import WhyUsSection from '@/components/sections/WhyUsSection';
import AboutSection from '@/components/sections/AboutSection';
import TechStackSection from '@/components/sections/TechStackSection';
import ProcessSection from '@/components/sections/ProcessSection';
import PlaygroundSection from '@/components/sections/PlaygroundSection';
import ROICalculatorSection from '@/components/sections/ROICalculatorSection';
import ConfiguratorSection from '@/components/sections/ConfiguratorSection';
import TeamSection from '@/components/sections/TeamSection';
import BlogSection from '@/components/sections/BlogSection';
import NewsletterSection from '@/components/sections/NewsletterSection';
import ContactSection from '@/components/sections/ContactSection';

const schemaGraph = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': 'https://add-individual-solutions.com/#organization',
      name: 'ADD Individual Solutions',
      alternateName: 'ADD.IS',
      url: 'https://add-individual-solutions.com',
      logo: {
        '@type': 'ImageObject',
        '@id': 'https://add-individual-solutions.com/#logo',
        url: 'https://add-individual-solutions.com/logo.jpg',
        caption: 'ADD Individual Solutions',
      },
      sameAs: [
        'https://www.linkedin.com/company/add-individual-solutions',
        'https://www.facebook.com/add.individual.solutions/',
      ],
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'Customer Service',
        availableLanguage: ['English', 'Romanian'],
      },
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Pyla',
        addressLocality: 'Larnaca',
        addressCountry: 'CY',
      },
    },
    {
      '@type': 'WebSite',
      '@id': 'https://add-individual-solutions.com/#website',
      url: 'https://add-individual-solutions.com',
      name: 'ADD Individual Solutions',
      inLanguage: ['en', 'ro'],
      publisher: { '@id': 'https://add-individual-solutions.com/#organization' },
    },
    {
      '@type': 'LocalBusiness',
      name: 'ADD Individual Solutions',
      image: { '@id': 'https://add-individual-solutions.com/#logo' },
      url: 'https://add-individual-solutions.com',
      email: 'contact@add-individual-solutions.com',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Pyla',
        addressLocality: 'Larnaca',
        addressCountry: 'CY',
      },
      geo: { '@type': 'GeoCoordinates', latitude: 34.9833, longitude: 33.9667 },
      priceRange: '€€€',
    },
    ...[
      { type: 'LLM & Generative AI Development', desc: 'Custom large language models, RAG pipelines, and generative systems trained on your data and deployed in your infrastructure.' },
      { type: 'Machine Learning Engineering', desc: 'End-to-end ML pipelines — from data engineering to production-grade model deployment, monitoring, and continuous optimization.' },
      { type: 'Web Development', desc: 'High-performance web applications — SaaS platforms, enterprise systems, beautiful, fast, and built to scale.' },
      { type: 'Computer Vision', desc: 'Image recognition, object detection, and video intelligence integrated seamlessly into products and operational workflows.' },
      { type: 'AI Automation & Agentic Systems', desc: 'Intelligent agents and multi-step orchestration that transform repetitive workflows into autonomous, self-optimizing systems.' },
      { type: 'AI Strategy & Consulting', desc: 'Roadmapping, technology selection, team training, and hands-on implementation guidance for AI adoption.' },
      { type: 'German-language Customer Support (CX Operations)', desc: 'Voice, e-mail, chat and messaging support in German — 1st and 2nd level, complaints and retention — delivered from Cyprus and Transylvania under one EU contract.' },
      { type: 'Multilingual Customer Support', desc: 'English as standard, Romanian, Greek and further EU languages on request, under one quality framework.' },
      { type: 'Back-office & Data Processing', desc: 'Order processing, KYC and onboarding, document handling, data entry and verification with four-eyes controls.' },
      { type: 'CX Consulting & AI-enabled CX', desc: 'Operational assessment, QA and WFM design, CSAT/NPS programmes; agent assist, intelligent deflection and automated quality scoring.' },
    ].map(s => ({
      '@type': 'Service',
      serviceType: s.type,
      description: s.desc,
      provider: { '@id': 'https://add-individual-solutions.com/#organization' },
      areaServed: [
        { '@type': 'Country', name: 'Cyprus' },
        { '@type': 'Place', name: 'Europe' },
      ],
    })),
    {
      '@type': 'Person',
      '@id': 'https://add-individual-solutions.com/#daniel',
      name: 'Daniel Dobos',
      jobTitle: 'Co-Founder & CTO',
      worksFor: { '@id': 'https://add-individual-solutions.com/#organization' },
    },
    ...[
      { name: 'Founder & CEO', jobTitle: 'Chief Executive Officer' },
      { name: 'UX/UI Lead', jobTitle: 'Design Director' },
      { name: 'ML Engineering Lead', jobTitle: 'Head of Production' },
    ].map(p => ({
      '@type': 'Person',
      name: p.name,
      jobTitle: p.jobTitle,
      worksFor: { '@id': 'https://add-individual-solutions.com/#organization' },
    })),
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://add-individual-solutions.com/' },
        { '@type': 'ListItem', position: 2, name: 'Services', item: 'https://add-individual-solutions.com/#services' },
        { '@type': 'ListItem', position: 3, name: 'CX Services', item: 'https://add-individual-solutions.com/cx' },
        { '@type': 'ListItem', position: 4, name: 'LLM Development', item: 'https://add-individual-solutions.com/services/llm-development' },
        { '@type': 'ListItem', position: 5, name: 'About', item: 'https://add-individual-solutions.com/#about' },
        { '@type': 'ListItem', position: 6, name: 'Blog', item: 'https://add-individual-solutions.com/blog' },
        { '@type': 'ListItem', position: 7, name: 'Contact', item: 'https://add-individual-solutions.com/#contact' },
      ],
    },
  ],
};

const Index = () => {
  return (
    <LanguageProvider>
      <CustomCursor />
      <Navbar />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaGraph) }} />
      <main>
        <HeroSection />
        <TickerSection />
        <ServicesSection />
        <CxTeaserSection />
        <NumbersSection />
        <WhyUsSection />
        <AboutSection />
        <TechStackSection />
        <ProcessSection />
        <PlaygroundSection />
        <ROICalculatorSection />
        <ConfiguratorSection />
        <TeamSection />
        <BlogSection />
        <NewsletterSection />
        <ContactSection />
      </main>
      <Footer />
      <AlexChat />
    </LanguageProvider>
  );
};

export default Index;
